// Context sources (ADR-0173 fase 2, PR-1) — the client's uploaded documents
// and the vocabulary the recipe declares for them.
//
// Engine = shapes and invariants, no vertical slug anywhere. The RECIPE
// declares which source kinds exist for a vertical (facility: política,
// checklist_base, guia_semanal, grafica), each with its precedence rank,
// admitted MIME types and validity mode. The layer turns on for an
// organization when its recipe declares `contextSourceKinds` AND the org's
// `context_ingest` flag is on — never by slug (ADR-0019/0071/0087).
import { z } from "zod";
export const CONTEXT_SOURCE_STATUSES = [
    "uploaded",
    "extracting",
    "in_review",
    "active",
    "archived",
    "failed",
];
export const contextSourceStatusSchema = z.enum(CONTEXT_SOURCE_STATUSES);
/** Legal transitions, written once (core-api's CAS updates read this). */
export const CONTEXT_SOURCE_TRANSITIONS = {
    uploaded: ["extracting", "failed", "archived"],
    extracting: ["in_review", "failed"],
    in_review: ["active", "archived", "extracting"],
    active: ["archived"],
    archived: [],
    failed: ["extracting", "archived"],
};
export function canTransitionContextSource(from, to) {
    return CONTEXT_SOURCE_TRANSITIONS[from].includes(to);
}
/** Why an extraction failed — a closed enum so alerts and the panel can
 *  group by reason (exploración §15 Devops). */
export const CONTEXT_EXTRACTION_FAILURE_REASONS = [
    "download_failed",
    "magic_bytes",
    "xlsx_macros",
    "xlsx_external_links",
    "xlsx_zip_bomb",
    "pdf_javascript",
    "pdf_too_many_pages",
    "pdf_encrypted",
    "pdf_no_text",
    "unsupported_kind",
    "provider_error",
    "schema_invalid",
    "timeout",
];
export const contextExtractionFailureReasonSchema = z.enum(CONTEXT_EXTRACTION_FAILURE_REASONS);
export const CONTEXT_SOURCE_MIME_TYPES = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "image/png",
    "image/jpeg",
    "image/webp",
];
export const contextSourceMimeTypeSchema = z.enum(CONTEXT_SOURCE_MIME_TYPES);
/** `dated`: valid_from/valid_to are set by the uploader (a weekly guide).
 *  `until_replaced`: valid until a newer source of the same kind is
 *  activated (a policy, a base checklist). */
export const contextValidityModeSchema = z.enum(["dated", "until_replaced"]);
/** What a recipe declares per source kind. */
export const contextSourceKindSchema = z.object({
    key: z
        .string()
        .trim()
        .regex(/^[a-z][a-z0-9_]{1,31}$/),
    label: z.string().trim().min(1).max(60),
    /** Lower rank wins a conflict between rules of two sources (política 1,
     *  checklist_base 2, …). Resolved at ingest with human review, never in
     *  the prompt (exploración §6). */
    precedenceRank: z.number().int().min(1).max(99),
    mimeTypes: z.array(contextSourceMimeTypeSchema).min(1),
    validity: contextValidityModeSchema,
    /** Image kinds produce visual references (fase 3), text kinds produce rules. */
    produces: z.enum(["rules", "visual_refs"]),
});
export const contextSourceKindsSchema = z
    .array(contextSourceKindSchema)
    .min(1)
    .superRefine((kinds, ctx) => {
    const keys = new Set();
    const ranks = new Set();
    for (const k of kinds) {
        if (keys.has(k.key)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate source kind ${k.key}` });
        }
        if (ranks.has(k.precedenceRank)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `two source kinds share precedenceRank ${k.precedenceRank}`,
            });
        }
        keys.add(k.key);
        ranks.add(k.precedenceRank);
    }
});
/** Who the client holds responsible for a rule (facility: tienda | visual | ambos). */
export const ruleResponsibleSchema = z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{1,31}$/);
/** The source kind's declaration for a mime type: null when the recipe
 *  does not admit it for that kind. */
export function findContextSourceKind(kinds, key) {
    return kinds.find((k) => k.key === key) ?? null;
}
//# sourceMappingURL=context-source.js.map